import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import { Banner, Button, Empty, ErrorBanner, NumberInput, Panel, Spinner, TextInput } from '@shop/ui'

type Row = {
  id: string | null
  userId: string
  fullNameAr: string
  day: string
  checkIn: string | null
  checkOut: string | null
  workedHours: number | null
  overtimeHours: number
  overtimeOverride: number | null
  note: string | null
  openedShift: boolean
}

const hhmm = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Amman', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())
}

function shift(day: string, by: number): string {
  const [y, m, d] = day.split('-').map(Number)

  return new Date(Date.UTC(y, m - 1, d + by)).toISOString().slice(0, 10)
}

/** «09:30» في يومٍ بعينه ⇐ لحظةٌ بتوقيت عمّان (+03 طوالَ السنة منذ ٢٠٢٢). */
function moment(day: string, time: string): string | null {
  const t = time.trim()

  return /^\d{1,2}:\d{2}$/.test(t) ? `${day}T${t.padStart(5, '0')}:00+03:00` : null
}

/**
 * الدوام — <b>طلبُ المالك ٢٠٢٦-١٠-٠٨</b>.
 *
 * <p>
 * الموظّفُ يكبس «وصلت / روّحت» من نقطة البيع، وهنا يراجع المالك: يعدّل الوقتين،
 * ويكتب الساعاتِ الزيادة بيده إن أراد (تغلب الحساب)، ويسجّل يومًا لمن نسي.
 * اليومُ ٨ ساعاتٍ بـ٧ دنانير، وكلُّ ساعةٍ فوقها بدينار — من شاشة العمولات.
 * </p>
 */
export default function Attendance() {
  const { can } = useAuth()
  const [day, setDay] = useState(today())

  const rows = useQuery({
    queryKey: ['attendance', day],
    queryFn: () => api<Row[]>(`/api/v1/attendance?day=${day}`),
    enabled: can(Perm.attendancePunch),
  })

  if (!can(Perm.attendancePunch)) {
    return (
      <Panel title="الدوام">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «تسجيلُ الدوام».</Banner>
      </Panel>
    )
  }

  return (
    <Panel title="الدوام">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setDay(shift(day, -1))}>‹ اليوم السابق</Button>
        <TextInput value={day} onChange={setDay} placeholder="2026-10-08" />
        <Button onClick={() => setDay(shift(day, 1))}>اليوم التالي ›</Button>
        <Button onClick={() => setDay(today())}>اليوم</Button>
      </div>

      <p className="mt-2 text-xs text-ink-soft">
        الوقت مثل <b dir="ltr">09:30</b> أو <b dir="ltr">17:45</b>. «زيادة» فارغة = تُحسب لحالها (ما فوق ٨ ساعات).
        ومن داوم ونسي يسجّل: اكبس «سجّل داوم» ولو بلا أوقات.
      </p>

      <ErrorBanner error={rows.error} />

      {rows.isPending ? (
        <Spinner />
      ) : (rows.data ?? []).length === 0 ? (
        <Empty>لا موظّفين.</Empty>
      ) : (
        <ul className="mt-3 grid gap-2">
          {(rows.data ?? []).map((row) => (
            <Editor
              key={`${row.userId}-${day}-${row.id ?? 'new'}`}
              row={row}
              day={day}
              editable={can(Perm.attendanceEdit)}
            />
          ))}
        </ul>
      )}
    </Panel>
  )
}

function Editor({ row, day, editable }: { row: Row; day: string; editable: boolean }) {
  const queries = useQueryClient()

  const [checkIn, setCheckIn] = useState(row.checkIn ? hhmm.format(new Date(row.checkIn)) : '')
  const [checkOut, setCheckOut] = useState(row.checkOut ? hhmm.format(new Date(row.checkOut)) : '')
  const [overtime, setOvertime] = useState(row.overtimeOverride === null ? '' : String(row.overtimeOverride))
  const [note, setNote] = useState(row.note ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const present = row.id !== null

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    setSaved(false)

    try {
      await work()
      setSaved(true)
      await Promise.all([
        queries.invalidateQueries({ queryKey: ['attendance', day] }),
        queries.invalidateQueries({ queryKey: ['commissions-daily'] }),
        queries.invalidateQueries({ queryKey: ['balances'] }),
      ])
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const save = () => run(() => api('/api/v1/attendance', {
    method: 'PUT',
    body: {
      userId: row.userId,
      day,
      checkIn: moment(day, checkIn),
      checkOut: moment(day, checkOut),
      overtimeHours: overtime.trim() === '' ? null : toNumber(overtime) ?? 0,
      note: note.trim() || null,
    },
  }))

  const remove = () => run(() => api(`/api/v1/attendance/${row.id}`, { method: 'DELETE' }))

  const status = present
    ? [
        'داوم',
        row.workedHours !== null ? `${row.workedHours} ساعة` : null,
        row.overtimeHours > 0 ? `زيادة ${row.overtimeHours} س` : null,
      ].filter(Boolean).join(' · ')
    : row.openedShift ? 'فتح ورديّة ولم يسجّل دوامه — بينحسب مداوم' : 'ما داوم'

  return (
    <li className={`rounded-xl border p-3 ${present ? 'border-ok/40 bg-ok/5' : 'border-line'}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <b className="min-w-0 flex-1">{row.fullNameAr}</b>
        <span className="text-xs text-ink-soft">{status}</span>
      </div>

      {editable && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <label className="grid gap-1 text-xs">
              وصل
              <TextInput value={checkIn} onChange={setCheckIn} placeholder="10:00" />
            </label>
            <label className="grid gap-1 text-xs">
              روّح
              <TextInput value={checkOut} onChange={setCheckOut} placeholder="18:00" />
            </label>
            <label className="grid gap-1 text-xs">
              زيادة (ساعات)
              <NumberInput value={overtime} onChange={setOvertime} placeholder={String(row.overtimeHours)} />
            </label>
            <label className="grid gap-1 text-xs sm:col-span-2">
              ملاحظة
              <TextInput value={note} onChange={setNote} placeholder="اختياريّ" />
            </label>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button tone="primary" disabled={busy} onClick={() => void save()}>
              {present ? 'احفظ' : 'سجّل داوم'}
            </Button>
            {present && (
              <Button disabled={busy} onClick={() => void remove()}>ما داوم — امسح</Button>
            )}
            {saved && <span className="text-sm text-ok">حُفظ.</span>}
          </div>
        </>
      )}

      <ErrorBanner error={error} />
    </li>
  )
}
